import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Modal, Text, Spacer } from '@Sage/hoi-poi-ui';
import AutomationsTemplates from 'containers/settings/AutomationSettings/AutomationsTemplates';
import AutomationBuilder from 'containers/settings/AutomationSettings/components/AutomationBuilder';
import { AUTOMATIONS_SETTINGS } from 'constants/Features';
import { getLiteral } from 'utils/getLiteral';
import { ensureRoute } from 'utils/routes';
import { logEvent } from 'utils/tracking';

import './styles.scss';

const AutomationEntityMenu = memo(({ onRef, buttonClassName, entity }) => {
    const [isOpen, setIsOpen] = useState(false);
    const builderRef = useRef(null);
    const { isAdmin, automationsSettings, automationsEnabled } = useSelector((state) => ({
        isAdmin: state.config?.permission?.canConfigImplementation,
        automationsSettings: state.config?.flagsmith?.[AUTOMATIONS_SETTINGS],
        automationsEnabled: state.config?.userData?.automationsEnabled,
    }));

    const isAllowed = useMemo(
        () => isAdmin && (automationsSettings || automationsEnabled),
        [isAdmin, automationsSettings, automationsEnabled],
    );

    const onBuilderRef = useCallback((ref) => {
        builderRef.current = ref;
    }, []);

    useEffect(() => {
        onRef &&
            onRef({
                open(...args) {
                    setIsOpen(true);
                    if (args && args.length) {
                        setTimeout(() => builderRef.current?.open(...args), 0);
                    }
                },
                close() {
                    setIsOpen(false);
                },
            });
    }, [onRef]);

    const openBuilder = useCallback((...args) => {
        setIsOpen(false);
        setTimeout(() => builderRef.current?.open(...args), 0);
    }, []);

    const triggerButton = useMemo(() => {
        return (
            <Button
                className={buttonClassName}
                type="terciary"
                icon="automations"
                onClick={() => setIsOpen(true)}
            />
        );
    }, [buttonClassName]);

    const requestClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const onConfirm = useCallback(() => {
        ensureRoute('/settings/automations/templates');
        logEvent({
            event: entity?.trueName || 'settings',
            submodule: 'automationsTemplate',
            functionality: 'seeAll',
        });
    }, [entity?.trueName]);

    const classes = useMemo(
        () => ({
            content: 'fm-automation-entity-menu__content',
        }),
        [],
    );

    if (!isAllowed) return null;

    return (
        <>
            {triggerButton}
            <Modal
                isOpen={isOpen}
                useHeader
                title={getLiteral('label_automations')}
                onRequestClose={requestClose}
                onCancel={requestClose}
                onConfirm={onConfirm}
                size="full"
                classes={classes}
                cancelText={getLiteral('action_cancel')}
                confirmText={getLiteral('action_automations_see_all_templates')}
            >
                <Text>{getLiteral('label_automations_desc')}</Text>
                <Spacer y={3} />
                <AutomationsTemplates openBuilder={openBuilder} defaultEntity={entity?.trueName} />
            </Modal>
            <AutomationBuilder onRef={onBuilderRef} />
        </>
    );
});

export default AutomationEntityMenu;
